import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Editor.css';
import {
  Play, Edit2, Copy, Save, ChevronLeft, User, FileText, Settings2, CheckCircle
} from 'lucide-react';

const API = '/api/editor';

async function apiFetch(method, path, body) {
  const opts = { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include' };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API}${path}`, opts);
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
  return json;
}

// ── Toast ────────────────────────────────────────────────────────────────────
function Toast({ msg, isError, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2600); return () => clearTimeout(t); }, [onDone]);
  return <div className={`ed-toast${isError ? ' error' : ''}`}>{msg}</div>;
}

// ── Login ────────────────────────────────────────────────────────────────────
function EditorLogin({ onLogin }) {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async e => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const d = await apiFetch('POST', '/login', { username: user, password: pass });
      onLogin(d.user);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="ed-login-shell">
      <form className="ed-login-card" onSubmit={submit}>
        <div className="ed-login-logo">
          <h1>The Differential</h1>
          <p>Case Editor — Authorized Access Only</p>
        </div>
        {err && <div className="ed-error">{err}</div>}
        <div className="ed-field">
          <label className="ed-label">Username</label>
          <input className="ed-input" value={user} onChange={e => setUser(e.target.value)} placeholder="naveen or divya" autoComplete="username" />
        </div>
        <div className="ed-field">
          <label className="ed-label">Password</label>
          <input className="ed-input" type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="••••••••••••" autoComplete="current-password" />
        </div>
        <button className="ed-btn" type="submit" disabled={busy || !user || !pass}>
          {busy ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

// ── Case Card (dashboard) ────────────────────────────────────────────────────
function CaseCard({ c, todayDay, onEdit, onPlay }) {
  const isToday = c.day === todayDay;
  const isTomorrow = c.day === todayDay + 1;
  const dayLabel = isToday ? 'TODAY' : isTomorrow ? 'TOMORROW' : `Day ${c.day}`;
  const dayCls = isToday ? 'today' : isTomorrow ? 'tomorrow' : '';

  // Status badge: reviewed > draft, special label for today/tomorrow
  const status = c.status || 'draft';
  const statusLabel = isToday ? 'Live Now' : isTomorrow ? 'Up Next' : status === 'reviewed' ? 'Ready ✓' : 'Draft';
  const statusCls = isToday ? 'live' : isTomorrow ? 'next' : status === 'reviewed' ? 'ready' : 'draft';

  const complaint = c.patient?.chief_complaint || '';
  const snippet = complaint.length > 70 ? complaint.slice(0, 70) + '…' : complaint || '—';

  return (
    <div className={`ed-case-card ${dayCls}`}>
      <div className="ed-card-top">
        <span className={`ed-card-day ${dayCls}`}>{dayLabel}</span>
        <span className={`ed-status-badge ${statusCls}`}>{statusLabel}</span>
      </div>
      <div className="ed-card-complaint">{snippet}</div>
      <div className="ed-card-actions">
        <button className="ed-btn small secondary" onClick={() => onEdit(c.day)}><Edit2 size={12} style={{ marginRight: 4 }} /> Edit</button>
        <button className="ed-btn small blue" onClick={() => onPlay(c.day)}><Play size={12} style={{ marginRight: 4, fill: 'currentColor' }} /> Play</button>
      </div>
    </div>
  );
}

// ── Generic typeahead picker ──────────────────────────────────────────────────
// Works like the game's order input: shows current value, filters as you type,
// pick replaces value, backspace lets you retype.
function TypeAhead({ value, placeholder, items, getLabel, getSub, onSelect, autoFocus }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Keep in sync if parent value changes (e.g. initial load)
  useEffect(() => { setQuery(value || ''); }, [value]);
  useEffect(() => { if (autoFocus && ref.current) { ref.current.focus(); } }, [autoFocus]);

  const filtered = query.trim().length === 0
    ? items.slice(0, 30)
    : items.filter(it => {
      const q = query.toLowerCase();
      const labelHit = getLabel(it).toLowerCase().includes(q);
      const subHit = getSub ? (getSub(it) || '').toLowerCase().includes(q) : false;
      return labelHit || subHit;
    }).slice(0, 30);

  const pick = (it) => {
    const label = getLabel(it);
    setQuery(label);
    setOpen(false);
    onSelect(it);
  };

  return (
    <div className="ed-picker-wrap">
      <input
        ref={ref}
        className="ed-input"
        value={query}
        placeholder={placeholder}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        autoComplete="off"
        spellCheck="false"
      />
      {open && (
        <div className="ed-picker-drop">
          {filtered.length > 0 ? filtered.map((it, i) => (
            <div key={i} className="ed-picker-item" onMouseDown={e => { e.preventDefault(); pick(it); }}>
              <span className="ed-picker-name">{getLabel(it)}</span>
              {getSub && <span className="ed-picker-cat">{getSub(it) || ''}</span>}
            </div>
          )) : (
            <div className="ed-picker-item" style={{ color: 'var(--ed-low)', pointerEvents: 'none' }}>
              No matches for "{query}" — try a shorter term
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Diagnosis typeahead (searches alias keys) ─────────────────────────────────
function DiagnosisPicker({ value, aliases, onChange }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const keys = Object.keys(aliases || {});
  const filtered = query.trim().length < 2
    ? []
    : keys.filter(k => k.toLowerCase().includes(query.toLowerCase())).slice(0, 12);

  const pick = (k) => {
    setQuery(k);
    setOpen(false);
    onChange(k);
  };

  return (
    <div className="ed-picker-wrap">
      <input
        className="ed-input"
        value={query}
        placeholder="e.g. Acute Inferior Myocardial Infarction"
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="ed-picker-drop">
          {filtered.map(k => (
            <div key={k} className="ed-picker-item" onMouseDown={e => { e.preventDefault(); pick(k); }}>
              <span className="ed-picker-name">{k}</span>
              <span className="ed-picker-cat">{(aliases[k] || []).slice(0, 2).join(', ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Move editor (expert / trap / relevant) ───────────────────────────────────
function MoveList({ moves, type, dictEntries, onChange }) {
  const label = type === 'expert' ? 'Expert Move' : type === 'trap' ? 'Trap Move' : 'Relevant Move';
  const color = type === 'expert' ? 'var(--ed-green)' : type === 'trap' ? 'var(--ed-amber)' : 'var(--ed-blue)';
  const f1 = type === 'expert' ? 'ego_boost' : type === 'trap' ? 'snark' : 'comment';
  const f1Label = type === 'expert' ? 'Ego Boost (shown when correct)' : type === 'trap' ? 'Snark (shown when trap hit)' : 'Comment (shown in feed)';
  const f1Placeholder = type === 'expert'
    ? 'You caught the key finding. Textbook move.'
    : type === 'trap'
      ? 'Classic overthinker. This test changed nothing and cost 6 hours.'
      : 'Relevant but not diagnostic on its own.';

  const [adding, setAdding] = useState(false);

  const entries = Object.entries(moves || {});

  const update = (key, field, val) => {
    onChange({ ...moves, [key]: { ...moves[key], [field]: val } });
  };

  const remove = key => {
    const n = { ...moves };
    delete n[key];
    onChange(n);
  };

  // Rename the key when user picks a different test from the typeahead
  const rename = (oldKey, newSlug) => {
    if (!newSlug || oldKey === newSlug) return;
    const n = {};
    Object.entries(moves).forEach(([k, v]) => { n[k === oldKey ? newSlug : k] = v; });
    onChange(n);
  };

  // Add new move — only after picking from dict
  const commitAdd = (dictEntry) => {
    const slug = dictEntry.slug;
    if (moves[slug]) { setAdding(false); return; } // already exists
    onChange({ ...moves, [slug]: { result: '', [f1]: '' } });
    setAdding(false);
  };

  return (
    <div className="ed-moves-list">
      {entries.map(([key, val]) => {
        const dictMatch = dictEntries.find(d => d.slug === key);
        const displayName = dictMatch ? dictMatch.displayName : key.replace(/_/g, ' ');
        return (
          <div key={key} className={`ed-move-card ${type}`}>
            <div className="ed-move-header">
              <div style={{ flex: 1 }}>
                <span className={`ed-move-label ${type}`}>{label}</span>
              </div>
              <button className="ed-remove-btn" onClick={() => remove(key)}>Remove</button>
            </div>
            {/* Test selector — typeahead, pre-filled with current name */}
            <div className="ed-field">
              <label className="ed-label">Test / Lab / Exam</label>
              <TypeAhead
                value={displayName}
                placeholder="Type to search and change…"
                items={dictEntries}
                getLabel={d => d.displayName}
                getSub={d => d.category}
                onSelect={d => rename(key, d.slug)}
              />
            </div>
            <div className="ed-field">
              <label className="ed-label">Result (findings shown to player)</label>
              <textarea className="ed-textarea" rows={2} value={val.result || ''}
                onChange={e => update(key, 'result', e.target.value)}
                placeholder="e.g. Troponin I: 1,840 ng/L (Ref: <14 ng/L)" />
            </div>
            <div className="ed-field">
              <label className="ed-label">{f1Label}</label>
              <textarea className="ed-textarea" rows={2} value={val[f1] || ''}
                onChange={e => update(key, f1, e.target.value)}
                placeholder={f1Placeholder} />
            </div>
          </div>
        );
      })}

      {/* Add new — must pick from dict first */}
      {adding ? (
        <div className="ed-add-picker-card">
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color, marginBottom: 6 }}>
            Select a {label.toLowerCase()} from the dictionary:
          </div>
          <TypeAhead
            value=""
            placeholder="Type to search (e.g. ECG, Troponin, CT chest…)"
            items={dictEntries}
            getLabel={d => d.displayName}
            getSub={d => d.category}
            onSelect={commitAdd}
            autoFocus
          />
          <button className="ed-remove-btn" style={{ marginTop: 8 }} onClick={() => setAdding(false)}>Cancel</button>
        </div>
      ) : (
        <button className="ed-add-move-btn" onClick={() => setAdding(true)}>+ Add {label}</button>
      )}
    </div>
  );
}

// ── Alias Editor ─────────────────────────────────────────────────────────────
function AliasEditor({ aliases, onSave, toast }) {
  const [local, setLocal] = useState(() => JSON.parse(JSON.stringify(aliases)));
  const [newDx, setNewDx] = useState('');
  const [newAlias, setNewAlias] = useState({});
  const [saving, setSaving] = useState(false);

  const addAlias = (dx, val) => {
    if (!val.trim()) return;
    setLocal(p => ({ ...p, [dx]: [...(p[dx] || []), val.trim().toLowerCase()] }));
    setNewAlias(p => ({ ...p, [dx]: '' }));
  };

  const removeAlias = (dx, idx) => {
    setLocal(p => ({ ...p, [dx]: p[dx].filter((_, i) => i !== idx) }));
  };

  const addDx = () => {
    if (!newDx.trim()) return;
    setLocal(p => ({ ...p, [newDx.trim()]: [] }));
    setNewDx('');
  };

  const removeDx = dx => {
    const n = { ...local };
    delete n[dx];
    setLocal(n);
  };

  const save = async () => {
    setSaving(true);
    try { await apiFetch('PUT', '/aliases', { aliases: local }); toast('Aliases saved!'); onSave(local); }
    catch (e) { toast(e.message, true); }
    finally { setSaving(false); }
  };

  return (
    <div className="ed-editor-shell">
      <div className="ed-editor-header">
        <div className="ed-editor-title">Diagnosis Aliases</div>
        <div className="ed-editor-actions">
          <button className="ed-btn small" onClick={save} disabled={saving}>{saving ? 'Saving…' : <><Save size={12} style={{ marginRight: 4 }} /> Save Aliases</>}</button>
        </div>
      </div>
      <div className="ed-section">
        <div className="ed-section-head">Add New Diagnosis</div>
        <div className="ed-alias-add-row">
          <input className="ed-input" value={newDx} onChange={e => setNewDx(e.target.value)}
            placeholder="Exact diagnosis string (must match cases.json)" />
          <button className="ed-btn small" onClick={addDx}>Add</button>
        </div>
      </div>
      <div className="ed-alias-list">
        {Object.entries(local).map(([dx, list]) => (
          <div key={dx} className="ed-alias-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="ed-alias-dx">{dx}</div>
              <button className="ed-remove-btn" style={{ marginLeft: 'auto' }} onClick={() => removeDx(dx)}>Remove</button>
            </div>
            <div className="ed-alias-chips">
              {list.map((a, i) => (
                <div key={i} className="ed-alias-chip">
                  {a}
                  <button className="ed-chip-remove" onClick={() => removeAlias(dx, i)}>×</button>
                </div>
              ))}
            </div>
            <div className="ed-alias-add-row">
              <input className="ed-input" style={{ fontSize: '0.75rem', padding: '5px 10px' }}
                value={newAlias[dx] || ''} onChange={e => setNewAlias(p => ({ ...p, [dx]: e.target.value }))}
                placeholder="Add alias (e.g. stemi, heart attack…)"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAlias(dx, newAlias[dx] || ''); } }}
              />
              <button className="ed-btn small secondary" onClick={() => addAlias(dx, newAlias[dx] || '')}>Add</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Case Editor ──────────────────────────────────────────────────────────────
function CaseEditorForm({ day, dictEntries, aliases, onBack, onSaved, onPlay, toast }) {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyJson = () => {
    if (!caseData) return;
    navigator.clipboard.writeText(JSON.stringify(caseData, null, 2))
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => toast('Could not copy to clipboard', true));
  };

  useEffect(() => {
    setLoading(true);
    apiFetch('GET', `/cases/${day}`)
      .then(d => setCaseData(JSON.parse(JSON.stringify(d.case))))
      .catch(() => {
        // New case scaffold
        setCaseData({
          day, patient: {
            age: '', sex: 'F', chief_complaint: '', history: '', physical_exam: '',
            vitals: { heart_rate: '', blood_pressure: '', respiratory_rate: '', spo2: '', temperature: '', gcs: '' }
          },
          exact_diagnosis: '', expert_moves: {}, trap_moves: {}, relevant_moves: {},
          win_message: '',
          disease_presentations: { diagnosis: '', manifestations: [] },
        });
      })
      .finally(() => setLoading(false));
  }, [day]);

  const set = (path, val) => {
    setCaseData(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = n;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = val;
      return n;
    });
  };

  const [saveOk, setSaveOk] = useState(false);

  const save = async () => {
    setSaving(true); setSaveOk(false);
    try {
      const toSave = { ...caseData, status: 'reviewed' };
      await apiFetch('PUT', `/cases/${day}`, { case: toSave });
      setCaseData(toSave);
      setSaveOk(!saveOk);
      toast(`Day ${day} saved ✓`);
      setTimeout(() => setSaveOk(false), 3000);
      onSaved();
    } catch (e) { toast(e.message, true); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="ed-loading"><div className="ed-spinner" /><span>Loading case…</span></div>;
  if (!caseData) return null;

  const p = caseData.patient || {};
  const vitals = p.vitals && typeof p.vitals === 'object' ? p.vitals : {};

  return (
    <div className="ed-editor-shell">
      <div className="ed-editor-header">
        <button className="ed-btn small secondary" onClick={onBack}><ChevronLeft size={14} style={{ marginRight: 4 }} /> Back</button>
        <div className="ed-editor-title">Day {day} — {caseData.exact_diagnosis || 'New Case'}</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="ed-btn small secondary" onClick={copyJson}>
            {copied ? <><CheckCircle size={12} style={{ marginRight: 4 }} /> Copied!</> : <><Copy size={12} style={{ marginRight: 4 }} /> Copy JSON</>}
          </button>
          <button className="ed-btn small blue" onClick={() => onPlay(day)}><Play size={12} style={{ marginRight: 4, fill: 'currentColor' }} /> Play</button>
          <button className="ed-btn small" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : <><Save size={12} style={{ marginRight: 4 }} /> Save Changes</>}
          </button>
        </div>
      </div>

      {/* Patient */}
      <div className="ed-section">
        <div className="ed-section-head">Patient Details</div>
        <div className="ed-row">
          <div className="ed-field">
            <label className="ed-label">Age</label>
            <input className="ed-input" type="number" value={p.age || ''} onChange={e => set('patient.age', parseInt(e.target.value) || '')} placeholder="68" />
          </div>
          <div className="ed-field">
            <label className="ed-label">Sex</label>
            <select className="ed-select" value={p.sex || 'F'} onChange={e => set('patient.sex', e.target.value)}>
              <option value="F">Female</option>
              <option value="M">Male</option>
            </select>
          </div>
        </div>
        <div className="ed-field">
          <label className="ed-label">Chief Complaint (shown to player immediately)</label>
          <textarea className="ed-textarea" rows={3} value={p.chief_complaint || ''} onChange={e => set('patient.chief_complaint', e.target.value)} placeholder="How the patient presents…" />
        </div>
        <div className="ed-field">
          <label className="ed-label">History & Background (shown when patient panel opened)</label>
          <textarea className="ed-textarea" rows={4} value={p.history || ''} onChange={e => set('patient.history', e.target.value)} placeholder="Past medical history, medications, social history…" />
        </div>
        <div className="ed-field">
          <label className="ed-label">Examination Findings (physical exam on inspection)</label>
          <textarea className="ed-textarea" rows={3} value={p.physical_exam || ''} onChange={e => set('patient.physical_exam', e.target.value)} placeholder="What you see on examination…" />
        </div>
        <div className="ed-section-head" style={{ marginTop: 4 }}>Vitals (always visible to player)</div>
        <div className="ed-row three">
          {[['HR', 'heart_rate', '110 bpm'], ['BP', 'blood_pressure', '95/60 mmHg'], ['RR', 'respiratory_rate', '20 breaths/min'], ['SpO2', 'spo2', '99% on room air'], ['Temp', 'temperature', '98.0 F'], ['GCS', 'gcs', '15']].map(([lbl, k, ph]) => (
            <div key={k} className="ed-field">
              <label className="ed-label">{lbl}</label>
              <input className="ed-input" value={vitals[k] || ''} onChange={e => set(`patient.vitals.${k}`, e.target.value)} placeholder={ph} />
            </div>
          ))}
        </div>
      </div>

      {/* Diagnosis + inline aliases */}
      <div className="ed-section">
        <div className="ed-section-head">Diagnosis</div>
        <div className="ed-field">
          <label className="ed-label">Exact Diagnosis — type to see suggestions from your alias library</label>
          <DiagnosisPicker
            value={caseData.exact_diagnosis || ''}
            aliases={aliases}
            onChange={v => set('exact_diagnosis', v)}
          />
          <div style={{ fontSize: '0.62rem', color: 'var(--ed-low)', marginTop: 3 }}>
            Must match a key in diagnosisAliases. Pick from dropdown or type a new one.
          </div>
        </div>
        <div className="ed-field">
          <label className="ed-label">Win Message (shown when player gets it right)</label>
          <textarea className="ed-textarea" rows={2} value={caseData.win_message || ''} onChange={e => set('win_message', e.target.value)} placeholder="Congratulations message…" />
        </div>
        {caseData.exact_diagnosis && (
          <div className="ed-field">
            <label className="ed-label">Accepted aliases for this diagnosis</label>
            <div className="ed-alias-chips" style={{ marginBottom: 4 }}>
              {(aliases[caseData.exact_diagnosis] || []).length === 0
                ? <span style={{ fontSize: '0.7rem', color: 'var(--ed-low)' }}>None yet — go to "Diagnosis Aliases" tab to add them.</span>
                : (aliases[caseData.exact_diagnosis] || []).map((a, i) => (
                  <div key={i} className="ed-alias-chip">{a}</div>
                ))
              }
            </div>
            <div style={{ fontSize: '0.62rem', color: 'var(--ed-low)' }}>
              These let players type "MI", "heart attack", etc. and still get marked correct. Manage them in the Aliases tab.
            </div>
          </div>
        )}
      </div>

      {/* Expert Moves */}
      <div className="ed-section">
        <div className="ed-section-head">Expert Moves — Correct Tests (no penalty)</div>
        <MoveList moves={caseData.expert_moves} type="expert" dictEntries={dictEntries}
          onChange={v => set('expert_moves', v)} />
      </div>

      {/* Trap Moves */}
      <div className="ed-section">
        <div className="ed-section-head">Trap Moves — Wrong Tests (−10 pts each)</div>
        <MoveList moves={caseData.trap_moves} type="trap" dictEntries={dictEntries}
          onChange={v => set('trap_moves', v)} />
      </div>

      {/* Relevant Moves */}
      <div className="ed-section">
        <div className="ed-section-head">Relevant Moves — Informative but not diagnostic (no penalty)</div>
        <MoveList moves={caseData.relevant_moves || {}} type="relevant" dictEntries={dictEntries}
          onChange={v => set('relevant_moves', v)} />
      </div>

      {/* Disease Presentation */}
      <div className="ed-section">
        <div className="ed-section-head">Disease Presentation (shown after game ends)</div>
        <div className="ed-field">
          <label className="ed-label">Diagnosis label (matches exact_diagnosis)</label>
          <input className="ed-input"
            value={caseData.disease_presentations?.diagnosis || caseData.exact_diagnosis || ''}
            onChange={e => set('disease_presentations.diagnosis', e.target.value)}
            placeholder="e.g. Cerebellar Infarction" />
        </div>
        <div className="ed-field">
          <label className="ed-label">Key Manifestations — one per line</label>
          <textarea className="ed-textarea" rows={8}
            value={(caseData.disease_presentations?.manifestations || []).join('\n')}
            onChange={e => set('disease_presentations.manifestations', e.target.value.split('\n'))}
            placeholder={"Sudden onset of severe vertigo unrelated to head position.\nInability to walk or stand without support (truncal ataxia).\nDirection-changing nystagmus."}
          />
          <div style={{ fontSize: '0.62rem', color: 'var(--ed-low)', marginTop: 3 }}>
            These are shown to the player after the game as educational content. One manifestation per line.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="ed-btn small secondary" onClick={onBack}><ChevronLeft size={14} style={{ marginRight: 4 }} /> Back</button>
        <button className="ed-btn small blue" onClick={() => onPlay(day)}><Play size={12} style={{ marginRight: 4, fill: 'currentColor' }} /> Play This Case</button>
        <button className="ed-btn small" onClick={save} disabled={saving}>{saving ? 'Saving…' : <><Save size={12} style={{ marginRight: 4 }} /> Save Changes</>}</button>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ user, cases, todayDay, dictEntries, aliases, onLogout, onRefresh, toast }) {
  const [view, setView] = useState('cases');
  const [editDay, setEditDay] = useState(null);
  const [jumpDay, setJumpDay] = useState('');

  const playDay = (day) => {
    window.open(`/?playDay=${day}`, '_blank');
  };

  const handleLogout = async () => {
    await apiFetch('POST', '/logout').catch(() => { });
    onLogout();
  };

  const jumpTo = () => {
    const d = parseInt(jumpDay);
    if (d > 0) { setEditDay(d); setJumpDay(''); }
  };

  const handleSaved = () => {
    // Refresh the case list in background — don't navigate away from the editor
    onRefresh();
  };

  return (
    <div className="editor-root">
      {/* TOPBAR */}
      <div className="ed-topbar">
        <div className="ed-topbar-logo">The Differential</div>
        <div className="ed-topbar-user">
          <span className="ed-topbar-who"><User size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} /> {user.displayName}</span>
          <button className="ed-btn small secondary" onClick={handleLogout}>Sign Out</button>
        </div>
      </div>
      {/* TAB ROW */}
      <div className="ed-tabrow">
        <button className={`ed-tab${view === 'cases' ? ' active' : ''}`} onClick={() => { setView('cases'); setEditDay(null); }}>
          <FileText size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: '-3px' }} /> Cases
        </button>
        <button className={`ed-tab${view === 'aliases' ? ' active' : ''}`} onClick={() => { setView('aliases'); setEditDay(null); }}>
          <Settings2 size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: '-3px' }} /> Diagnosis Aliases
        </button>
      </div>

      <div className="ed-main">
        {view === 'aliases' ? (
          <AliasEditor aliases={aliases} onSave={() => { }} toast={toast} />
        ) : editDay !== null ? (
          <CaseEditorForm
            day={editDay}
            dictEntries={dictEntries}
            aliases={aliases}
            onBack={() => setEditDay(null)}
            onSaved={handleSaved}
            onPlay={playDay}
            toast={toast}
          />
        ) : (
          <>
            <div className="ed-toolbar">
              <div className="ed-toolbar-left">
                <span className="ed-page-head">All Cases</span>
                <span className="ed-today-hint">Today = Day {todayDay}</span>
              </div>
              <div className="ed-toolbar-right">
                <input className="ed-day-input" type="number" min="1" value={jumpDay}
                  onChange={e => setJumpDay(e.target.value)} placeholder="Day #"
                  onKeyDown={e => e.key === 'Enter' && jumpTo()} />
                <button className="ed-btn small" onClick={jumpTo}>Jump</button>
              </div>
            </div>
            <div className="ed-case-list">
              {cases.map(c => (
                <CaseCard key={c.day} c={c} todayDay={todayDay} onEdit={setEditDay} onPlay={playDay} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Root Editor ───────────────────────────────────────────────────────────────
export default function Editor() {
  const [authState, setAuthState] = useState('checking'); // checking | login | ready
  const [user, setUser] = useState(null);
  const [cases, setCases] = useState([]);
  const [aliases, setAliases] = useState({});
  const [dictEntries, setDictEntries] = useState([]);
  const [todayDay, setTodayDay] = useState(1);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, isError = false) => {
    setToast({ msg, isError });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  // Check auth on mount
  useEffect(() => {
    apiFetch('GET', '/me')
      .then(d => { setUser(d.user); loadData(); setAuthState('ready'); })
      .catch(() => setAuthState('login'));
  }, []);

  const loadData = () => {
    apiFetch('GET', '/cases').then(d => setCases(d.cases || [])).catch(() => { });
    apiFetch('GET', '/aliases').then(d => setAliases(d.aliases || {})).catch(() => { });
    apiFetch('GET', '/dictionary').then(d => setDictEntries(d.entries || [])).catch(() => { });
    // Calculate today's day number from server
    fetch('/api/health').then(r => r.json()).then(d => setTodayDay(d.day || 1)).catch(() => { });
  };

  const handleLogin = (u) => {
    setUser(u);
    loadData();
    setAuthState('ready');
  };

  const handleLogout = () => {
    setUser(null);
    setCases([]);
    setAuthState('login');
  };

  if (authState === 'checking') {
    return (
      <div className="editor-root">
        <div className="ed-loading"><div className="ed-spinner" /><span>Loading…</span></div>
      </div>
    );
  }

  if (authState === 'login') {
    return (
      <div className="editor-root">
        <EditorLogin onLogin={handleLogin} />
        {toast && <Toast msg={toast.msg} isError={toast.isError} onDone={dismissToast} />}
      </div>
    );
  }

  return (
    <>
      <Dashboard
        user={user}
        cases={cases}
        todayDay={todayDay}
        dictEntries={dictEntries}
        aliases={aliases}
        onLogout={handleLogout}
        onRefresh={loadData}
        toast={showToast}
      />
      {toast && <Toast msg={toast.msg} isError={toast.isError} onDone={dismissToast} />}
    </>
  );
}
